import { createSelector } from 'reselect';

import groupByDateKey from '../lib/groupByDateKey.js';
import sortByDateProp from '../lib/sortByDateProp.js';
import { mostRecentMessageReceivedSelector } from './lib/mostRecentMessageReceivedSelector.js';
import autoselectedFromPhoneSelector from '../screens/leadModal/redux/autoselectedFromPhoneSelector.js';
import activeComposerTypeSelector from './lib/activeComposerTypeSelector.js';
import autoselectedPhoneSelector from '../screens/leadModal/redux/autoselectedPhoneSelector.js';

import * as querySelectors from './querySelectors.js';
import * as serviceProviderSelectors from './serviceProviderSelectors.js';
import * as authSelectors from './authSelectors.js';
import * as leadSelectors from './leadSelectors.js';
import { activeLeadActivity } from './leadSelectors.js';
import inboxFolderSelector from '../screens/inbox/store/inboxFolderSelector.js';
import { creatingMessageId, notificationFolderId } from './querySelectors.js';
import eventsSelector from 'selectors/lib/eventsSelector.js';
import inboxFoldersSelector from 'screens/inbox/store/inboxFoldersSelector.js';
import getEventFolders from 'lib/getEventFolders.js';
import { serviceProviderLocations } from './serviceProviderSelectors.js';
import { activeLeadPipelineLeads } from './leadSelectors.js';
const stableArray = [];
const stableObject = {};

export const readOrUnread = (state) => (state.ui.filters.showReadMessages ? 'read' : 'unread');
export const statusFilter = (state) => state.ui.filters.status;

export const allMessagesRaw = (state) => state.messages.allMessages;
export const allMessages = createSelector(allMessagesRaw, (messages) => messages.filter((n) => n.event_status !== 'deleted'));
export const allNonLeadMessages = createSelector(allMessages, (allMessages) => allMessages.filter((n) => !n.lead_id));
export const communicationLogsMessages = createSelector(allMessages, statusFilter, (allMessages, statusFilter) => {
  return groupByDateKey(
    allMessages
      .filter((n) => n.data_source === 'communicationLogs')
      .filter((n) => {
        return statusFilter ? n.status_id === statusFilter : n;
      }),
    'creationDatetime'
  );
});

export const inboxCombinedMessages = (folderId) =>
  createSelector(allMessages, readOrUnread, (allMessages, readOrUnread) => {
    return allMessages //
      .filter((n) => n.data_source === `inbox_${folderId}`)
      .filter((n) => ((n.is_read || '0') === '0' ? 'unread' : 'read') === readOrUnread);
  });

export const chatMessages = createSelector(allMessages, querySelectors.eid, querySelectors.filter, activeLeadActivity, (allMessages, eid, filter, activity) => {
  let matches = allMessages.filter((n) => n.data_source === 'leadModalChat');
  if (matches.length < 1) {
    const firstMessage = allMessages.find((n) => n.id === eid && !n.lead_id);
    if (firstMessage && !firstMessage.thread_id) {
      matches = [firstMessage];
    } else {
      matches = firstMessage ? allMessages.filter((n) => n.thread_id === (firstMessage || stableObject).thread_id) : stableArray;
    }
  }
  // used to filter out any todos that are referenced in forms
  let todoIds = matches.filter((n) => !!n.todo_id).reduce((acc, n) => [...acc, n.todo_id], []);
  matches = matches.filter((n) => n.type !== 'todos' || (!todoIds.includes(n.id) && (n.todo_type === '9' && n.pipeline_lead_id === null ? true : ['1', '2'].includes(n.todo_status))));
  return [...matches, ...activity].sort(sortByDateProp('creationDatetime', 'asc')).filter((n) => (!filter || filter === 'all' ? true : n.type === filter));
});

const leadsDetails = (state) => state.reporting.leadsDetails;

export const leadsDetailsWithFilters = createSelector(leadsDetails, statusFilter, (leadsDetails, statusFilter) => {
  return (leadsDetails || []).filter((n) => {
    return statusFilter ? n.status_id === statusFilter : n;
  });
});

export const activeNonLeadMessage = createSelector(allNonLeadMessages, querySelectors.eid, leadsDetails, (messages, eid, leadsDetails) => (messages || stableArray).filter((n) => n.data_source === 'leadModalChat').find((n) => (n || stableObject).id === eid) || (messages || stableArray).find((n) => (n || stableObject).id === eid) || (leadsDetails || stableArray).find((n) => (n || stableObject).id === eid));
export const activeNonLeadMessageId = createSelector(activeNonLeadMessage, (msg) => (msg || stableObject).id);

export const rehydrationEventId = (state) => state.inboxScreen.rehydrationEventId;

export const mostRecentMessageReceived = mostRecentMessageReceivedSelector(chatMessages);
export const autoSelectedFromPhoneAndText = autoselectedFromPhoneSelector(mostRecentMessageReceived, serviceProviderSelectors.trackedPhoneNumbers, authSelectors.userId, querySelectors.query, authSelectors.defaultTrackedPhoneId, serviceProviderLocations, activeLeadPipelineLeads);

export const replyToEmail = createSelector(chatMessages, querySelectors.replyTo, (chatHistory, replyTo) => {
  return !replyTo ? undefined : (chatHistory || stableArray).find((n) => n.platform_message_id === replyTo);
});

export const messageCounts = (state) => state.messages.messageCounts;
export const messageOffsets = (state) => state.messages.messageOffsets;
export const folderOffset = (folder) => (state) => state.messages.messageOffsets[folder] || 0;

export const activeComposerType = activeComposerTypeSelector;
export const autoselectedPhone = autoselectedPhoneSelector(chatMessages, leadSelectors.activeLeadContacts);

export const folderEvents = createSelector(eventsSelector, inboxFoldersSelector, (events, folders) => {
  return (folders || stableArray).map((folder) => {
    if (!folder) {
      return { id: folder.id, events: stableArray, count: 0 };
    }
    const payload = (events || stableArray)
      .filter((n) => n.acknowledged !== true)
      .filter((n) => ((n || {}).event_data || {}).outgoing !== '1')
      .filter((n) => {
        const eventFolders = getEventFolders(folders, n);
        return eventFolders.includes(folder.id);
      });

    return { id: folder.id, events: payload, count: payload.length };
  });
});

export const creatingPipelineLeadMessage = createSelector(creatingMessageId, allMessages, (id, messages) => {
  return messages.find((n) => n.unique_id === id && n.data_source === 'leadModalChat');
});
